import { motion } from 'framer-motion';
import { ByteStream } from '@/components/ByteStream';
import { StaggerHeading } from '@/components/StaggerHeading';
import { DUR, EASE_OUT_QUINT } from '@/lib/motion';

/**
 * Open — the cinematic first frame.
 *
 * No CTAs. No stats line. No buttons. The byte stream runs in the
 * background forever; the headline lands; one quiet "keep scrolling" cue
 * sits at the bottom. Site-as-CTA: if the open frame doesn't earn the
 * scroll, nothing else will.
 */
export function Open() {
  return (
    <section
      id="open"
      className="relative flex flex-col"
      style={{ minHeight: '100dvh' }}
    >
      {/* Cinematic background loop */}
      <div className="absolute inset-0 overflow-hidden">
        <ByteStream />
        {/* Soft mask so the bytes fade into the page edges + behind text */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, color-mix(in oklch, var(--bg) 75%, transparent) 0%, color-mix(in oklch, var(--bg) 35%, transparent) 45%, transparent 80%)',
          }}
        />
      </div>

      {/* Header strip — small, top, mono microcopy */}
      <div className="relative z-10 pt-7">
        <div className="container-x flex items-center justify-between">
          <span
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--text-3)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
            }}
          >
            v0.3.0 · MIT · Made by Marko Slobođan
          </span>
          <a
            href="https://github.com/MarkoSlobodjan/slobozip"
            target="_blank"
            rel="noreferrer"
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--text-3)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
              transition: 'color var(--dur-fast) var(--ease-out-expo)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-3)')}
          >
            github ↗
          </a>
        </div>
      </div>

      {/* Headline column — centered vertically in the remaining space */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="container-x">
          <StaggerHeading
            as="h1"
            className="t-display"
            stagger={0.035}
            duration={1.0}
            style={{
              fontSize: 'var(--type-3xl)',
              color: 'var(--text)',
            }}
          >
            <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
              Compression,
            </span>
            <span
              className="t-italic-accent"
              style={{ color: 'var(--accent)', display: 'block', whiteSpace: 'nowrap' }}
            >
              hand-built.
            </span>
          </StaggerHeading>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT, delay: 0.5 }}
            style={{
              marginTop: 32,
              fontSize: 'var(--type-md)',
              color: 'var(--text-2)',
              lineHeight: 1.55,
              maxWidth: '52ch',
            }}
          >
            An LZSS engine written from scratch in C++. One core, four surfaces,
            a custom archive format. Everything you see below is running on the
            real thing.
          </motion.p>
        </div>
      </div>

      {/* Quiet scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: DUR.slow, delay: 1.1 }}
        className="relative z-10 pb-10"
      >
        <div className="container-x flex items-center justify-between">
          <span
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--text-3)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
            }}
          >
            keep scrolling
          </span>
          <ScrollCue />
        </div>
      </motion.div>
    </section>
  );
}

/** A 1px vertical line that breathes — slow, looping, not bouncy. */
function ScrollCue() {
  return (
    <span
      aria-hidden
      style={{ display: 'inline-block', width: 1, height: 32, position: 'relative' }}
    >
      <span
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--text-4)',
        }}
      />
      <span
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: '40%',
          background: 'var(--accent)',
          animation: 'cue 2.4s var(--ease-in-out-cubic) infinite',
        }}
      />
      <style>{`
        @keyframes cue {
          0%   { transform: translateY(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(250%); opacity: 0; }
        }
      `}</style>
    </span>
  );
}
